import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { subscriptionService } from '../../services/subscriptionService';
import analyticsService from '../../services/analyticsService';
import NavigationSidebar from'../../components/NavigationSidebar';
import Icon from'../../components/AppIcon';
 
import PricingCard from'./components/PricingCard';

const Pricing = () => {
  const navigate = useNavigate()
  const { user, isAuthenticated } = useAuth()
  const [plans, setPlans] = useState([])
  const [currentSubscription, setCurrentSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [processingPlan, setProcessingPlan] = useState(null)
  const [retryCount, setRetryCount] = useState(0)
  const [billingCycle, setBillingCycle] = useState('monthly')
  const [showProration, setShowProration] = useState(false)
  const [prorationData, setProrationData] = useState(null)

  useEffect(() => {
    loadPlans();
    if (isAuthenticated && user) {
      loadUserSubscription();
    } else if (!isAuthenticated) {
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  const loadPlans = async () => {
    try {
      const { data, error } = await subscriptionService?.getPlans();
      if (!error && data) {
        setPlans(data);
      }
    } catch (error) {
      console.error('Error loading plans:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  };

  const loadUserSubscription = async () => {
    if (!user?.id) return;
    try {
      const { data } = await subscriptionService?.getUserSubscription(user?.id);
      if (data) {
        setCurrentSubscription(data);
        setBillingCycle(data?.billing_cycle || 'monthly');
      }
    } catch (error) {
      console.error('Error loading user subscription:', error);
      setCurrentSubscription(null);
    }
  }

  const handleBillingCycleToggle = () => {
    setBillingCycle(prev => prev === 'monthly' ? 'annual' : 'monthly')
  }

  const calculateProration = async (plan) => {
    if (!user?.id || !currentSubscription) return null
    
    const { data } = await subscriptionService?.calculateProration(
      user?.id,
      plan?.id,
      billingCycle
    )
    
    return data
  }

  const handleSelectPlan = async (plan) => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: '/pricing' } })
      return
    }

    if (plan?.tier === 'free') {
      return
    }

    if (plan?.tier === 'enterprise') {
      // Track enterprise contact intent
      analyticsService?.trackUpgradeCTA('pricing_page_enterprise', currentSubscription?.tier || 'free')
      return
    }

    // Get appropriate price ID based on billing cycle
    const priceId = billingCycle === 'annual' ? plan?.annual_stripe_price_id : plan?.stripe_price_id

    if (!priceId) {
      alert('This plan is not available for checkout yet. Please contact support.')
      return
    }

    // Calculate proration if user has existing subscription
    if (currentSubscription && currentSubscription?.tier !== 'free') {
      const proration = await calculateProration(plan)
      if (proration) {
        setProrationData({
          plan,
          priceId,
          ...proration
        })
        setShowProration(true)
        return
      }
    }

    // Proceed with checkout
    await proceedToCheckout(plan, priceId)
  }

  const proceedToCheckout = async (plan, priceId) => {
    setProcessingPlan(plan?.id)
    const currentRetry = retryCount
    const currentTier = currentSubscription?.tier || 'free'
    const comparison = currentTier === 'free' ? 'upgrade' : plan?.tier === 'draftiq_plus' ? 'upgrade' : 'downgrade'
    const planPrice = billingCycle === 'annual' ? plan?.annual_price : plan?.price
    const currentPrice = currentSubscription?.subscription_plans?.price || 0
    const priceChange = planPrice - currentPrice
    
    try {
      const { data, error } = await subscriptionService?.createCheckoutSession(
        priceId,
        user?.id,
        billingCycle
      )

      if (error) throw error

      if (data?.url) {
        // Track subscription change initiation from pricing page
        analyticsService?.trackSubscriptionChange(
          comparison,
          currentTier,
          plan?.tier,
          priceChange
        )
        
        // If this was a retry, track successful recovery
        if (currentRetry > 0) {
          analyticsService?.trackErrorRecovery(
            'pricing_checkout_failed',
            'Checkout succeeded after retry',
            currentRetry,
            true,
            `${currentTier} to ${plan?.tier}`
          )
          setRetryCount(0)
        }
        
        window.location.href = data?.url
      }
    } catch (error) {
      const newRetryCount = currentRetry + 1
      setRetryCount(newRetryCount)
      
      console.error('Checkout error:', error)
      
      // Track checkout error from pricing page
      analyticsService?.trackErrorRecovery(
        'pricing_checkout_failed',
        error?.message || 'Failed to start checkout',
        newRetryCount,
        false,
        `${currentTier} to ${plan?.tier}`
      )
      
      // Track friction point
      analyticsService?.trackFrictionPoint(
        'pricing_page',
        'checkout_error',
        4,
        { from_plan: currentTier, to_plan: plan?.tier, retry_count: newRetryCount }
      )
      
      alert('Failed to start checkout. Please try again.')
    } finally {
      setProcessingPlan(null)
    }
  }

  const handleProrationConfirm = () => {
    if (prorationData) {
      proceedToCheckout(prorationData?.plan, prorationData?.priceId)
      setShowProration(false)
      setProrationData(null)
    }
  }

  const isCurrentPlan = (planTier) => {
    return currentSubscription?.tier === planTier && currentSubscription?.billing_cycle === billingCycle
  }

  const getDisplayPrice = (plan) => {
    if (billingCycle === 'annual' && plan?.annual_price) {
      return {
        price: plan?.annual_price,
        period: '/year',
        monthlyEquivalent: (plan?.annual_price / 12)?.toFixed(2),
        savings: plan?.annual_discount_percent
      };
    }
    return {
      price: plan?.price,
      period: '/month',
      monthlyEquivalent: null,
      savings: null
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen bg-background">
        <NavigationSidebar />
        <div className="flex-1 flex items-center justify-center ml-0 lg:ml-60">
          <div className="text-center">
            <Icon name="Loader" size={48} className="animate-spin mx-auto mb-4 text-primary" />
            <p className="text-muted-foreground">Loading pricing plans...</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5">
      <NavigationSidebar />
      <div className="lg:ml-64 min-h-screen">
        {/* Hero Section with Gradient */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-accent/5 pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
            <div className="text-center mb-12">
              <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-4 font-heading">
                Choose Your Plan
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Unlock powerful AI agents to transform your business operations
              </p>
            </div>

            {/* Billing Cycle Toggle with Glass Effect */}
            <div className="flex justify-center mb-12">
              <div className="glass-strong rounded-2xl p-2 inline-flex items-center gap-2 shadow-glass">
                <button
                  onClick={handleBillingCycleToggle}
                  className={`
                    px-6 py-3 rounded-xl font-medium transition-all duration-300
                    ${
                      billingCycle === 'monthly' ?'bg-primary text-primary-foreground shadow-lg' :'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  Monthly
                </button>
                <button
                  onClick={handleBillingCycleToggle}
                  className={`
                    px-6 py-3 rounded-xl font-medium transition-all duration-300 relative
                    ${
                      billingCycle === 'annual' ?'bg-primary text-primary-foreground shadow-lg' :'text-muted-foreground hover:text-foreground'
                    }
                  `}
                >
                  Annual
                  <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-semibold">
                    Save 20%
                  </span>
                </button>
              </div>
            </div>

            {/* Pricing Cards Grid */}
            {loading ? (
              <div className="flex justify-center items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
                {plans?.map((plan) => {
                  const displayPrice = getDisplayPrice(plan)
                  const isCurrentPlan = currentSubscription?.subscription_plan_id === plan?.id
                  const isProcessing = processingPlan === plan?.id

                  return (
                    <PricingCard
                      key={plan?.id}
                      plan={plan}
                      displayPrice={displayPrice}
                      billingCycle={billingCycle}
                      isCurrentPlan={isCurrentPlan}
                      isProcessing={isProcessing}
                      onSelect={() => handleSelectPlan(plan)}
                    />
                  )
                })}
              </div>
            )}

            {/* Feature Comparison Section with Elevated Card */}
            <div className="mt-20">
              <div className="elevated-card rounded-3xl p-8 sm:p-12 backdrop-blur-sm">
                <h2 className="text-3xl font-bold text-center text-foreground mb-8 font-heading">
                  All Plans Include
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {[
                    { icon: 'Zap', title: 'AI-Powered Agents', desc: 'Intelligent automation for your business' },
                    { icon: 'Shield', title: 'Enterprise Security', desc: 'Bank-level encryption and compliance' },
                    { icon: 'BarChart3', title: 'Advanced Analytics', desc: 'Real-time insights and reporting' },
                    { icon: 'Users', title: 'Team Collaboration', desc: 'Seamless multi-user workflows' },
                    { icon: 'Headphones', title: '24/7 Support', desc: 'Expert help whenever you need it' },
                    { icon: 'Rocket', title: 'Regular Updates', desc: 'New features and improvements' },
                  ]?.map((feature, index) => (
                    <div
                      key={index}
                      className="glass-subtle rounded-2xl p-6 hover-lift group"
                    >
                      <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                        <Icon name={feature?.icon} size={24} className="text-primary" />
                      </div>
                      <h3 className="text-lg font-semibold text-foreground mb-2 font-heading">
                        {feature?.title}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {feature?.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* FAQ Section with Glass Effect */}
            <div className="mt-20">
              <h2 className="text-3xl font-bold text-center text-foreground mb-12 font-heading">
                Frequently Asked Questions
              </h2>
              <div className="max-w-3xl mx-auto space-y-4">
                {[
                  {
                    q: 'Can I change my plan later?',
                    a: 'Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately with prorated billing.',
                  },
                  {
                    q: 'What payment methods do you accept?',
                    a: 'We accept all major credit cards, debit cards, and ACH transfers for enterprise plans.',
                  },
                  {
                    q: 'Is there a free trial?',
                    a: 'Yes! All new users start with our Free plan to explore the platform. Upgrade anytime to unlock more agents.',
                  },
                  {
                    q: 'What happens if I exceed my agent limit?',
                    a: 'You\'ll be prompted to upgrade to a higher tier. Your existing agents will continue working normally.',
                  },
                ]?.map((faq, index) => (
                  <div key={index} className="glass-strong rounded-2xl p-6 hover-lift">
                    <h3 className="text-lg font-semibold text-foreground mb-2 font-heading">
                      {faq?.q}
                    </h3>
                    <p className="text-muted-foreground">{faq?.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Pricing